import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { useNerfedMMVolumeWeightParams } from "../../hooks/api/admin/useNerfedMMVolumeWeightParams";
import {
  Actions,
  CardHeader,
  CardSubtitle,
  CardTitle,
  ErrorText,
  Grid,
  Input,
  Label,
  LabelHint,
  Summary,
  SummaryLabel,
  SummaryValue,
  ToolPanel,
} from "./style";

interface Form {
  zero_probability: string;
  max_weight: string;
}

const emptyForm: Form = {
  zero_probability: "",
  max_weight: "",
};

// Accept any decimal in [0, 1] (including 1.0, 0.123456789...). Empty
// becomes NaN so the consumer can flag it as invalid.
const parseFloatInput = (raw: string): number => {
  if (raw.trim() === "") return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
};

const fmt = (n: number, digits = 6): string =>
  Number.isFinite(n) ? n.toFixed(digits).replace(/\.?0+$/, "") || "0" : "—";

export function NerfedMMVolumeWeightParams() {
  const { data, isLoading, updateNerfedMMVolumeWeightParams } =
    useNerfedMMVolumeWeightParams();

  const [form, setForm] = useState<Form>(emptyForm);

  useEffect(() => {
    if (!data) return;
    setForm({
      zero_probability: String(data.zero_probability),
      max_weight: String(data.max_weight),
    });
  }, [data]);

  const parsed = useMemo(
    () => ({
      zero_probability: parseFloatInput(form.zero_probability),
      max_weight: parseFloatInput(form.max_weight),
    }),
    [form]
  );

  // zero_probability ∈ [0, 1], max_weight ∈ (0, 1] — same bounds the
  // backend enforces. Mirroring them client-side gives instant feedback
  // and prevents wasted POSTs.
  const zpInvalid =
    !Number.isFinite(parsed.zero_probability) ||
    parsed.zero_probability < 0 ||
    parsed.zero_probability > 1;
  const mwInvalid =
    !Number.isFinite(parsed.max_weight) ||
    parsed.max_weight <= 0 ||
    parsed.max_weight > 1;
  const anyInvalid = zpInvalid || mwInvalid;

  const expectedValue = useMemo(() => {
    if (anyInvalid) return NaN;
    return (1 - parsed.zero_probability) * (parsed.max_weight / 2);
  }, [anyInvalid, parsed]);

  const dirty = useMemo(() => {
    if (!data) return false;
    return (
      parsed.zero_probability !== data.zero_probability ||
      parsed.max_weight !== data.max_weight
    );
  }, [data, parsed]);

  const onChange = useCallback(
    (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      // Allow digits, single dot, optional sign-free decimals.
      if (v !== "" && !/^\d*\.?\d*$/.test(v)) return;
      setForm((prev) => ({ ...prev, [key]: v }));
    },
    []
  );

  const onSave = useCallback(async () => {
    if (anyInvalid) return;
    const confirmed = window.confirm(
      "Update nerfed MM volume weight params?\n\n" +
        `zero_probability: ${parsed.zero_probability}\n` +
        `max_weight: ${parsed.max_weight}\n` +
        `Expected per-trade weight: ${fmt(expectedValue)}\n\n` +
        "This takes effect immediately for new trades by accounts in NerfedMMAccounts. " +
        "Existing trades keep the weight they were stamped with."
    );
    if (!confirmed) return;
    await updateNerfedMMVolumeWeightParams({
      zero_probability: parsed.zero_probability,
      max_weight: parsed.max_weight,
    });
  }, [anyInvalid, expectedValue, parsed, updateNerfedMMVolumeWeightParams]);

  const onReset = useCallback(() => {
    if (!data) return;
    setForm({
      zero_probability: String(data.zero_probability),
      max_weight: String(data.max_weight),
    });
  }, [data]);

  return (
    <ToolPanel role="tabpanel">
      <CardHeader>
        <CardTitle>Nerfed MM Volume Weight</CardTitle>
        <CardSubtitle>
          Two knobs shaping the per-trade <strong>VolumeWeight</strong>{" "}
          distribution applied to perpetual trades by accounts in{" "}
          <code>NerfedMMAccounts</code>. Defaults <strong>(1/3, 0.75)</strong>{" "}
          give an expected per-trade weight of <strong>0.25</strong>, i.e.
          nerfed MM volume credits at ~25% of raw notional. Takes effect
          immediately on new trades.
        </CardSubtitle>
      </CardHeader>

      <Grid>
        <Label htmlFor="zero_probability">
          Zero Probability
          <LabelHint>
            Chance any single trade is zero-weighted. Must be in [0, 1].
          </LabelHint>
        </Label>
        <div>
          <Input
            id="zero_probability"
            hasErrors={zpInvalid}
            value={form.zero_probability}
            onChange={onChange("zero_probability")}
            placeholder={data ? String(data.zero_probability) : "0.333333"}
            inputMode="decimal"
            disabled={isLoading}
          />
          {zpInvalid && <ErrorText>Must be a number in [0, 1].</ErrorText>}
        </div>

        <Label htmlFor="max_weight">
          Max Weight
          <LabelHint>
            Upper bound for non-zero weights (uniform on [0, max_weight)). Must
            be in (0, 1].
          </LabelHint>
        </Label>
        <div>
          <Input
            id="max_weight"
            hasErrors={mwInvalid}
            value={form.max_weight}
            onChange={onChange("max_weight")}
            placeholder={data ? String(data.max_weight) : "0.75"}
            inputMode="decimal"
            disabled={isLoading}
          />
          {mwInvalid && <ErrorText>Must be a number in (0, 1].</ErrorText>}
        </div>
      </Grid>

      <Summary>
        <SummaryLabel>Expected per-trade weight</SummaryLabel>
        <SummaryValue valid={!anyInvalid}>{fmt(expectedValue)}</SummaryValue>
      </Summary>

      <Actions>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          onClick={onReset}
          disabled={isLoading || !dirty}
        >
          Reset
        </Button>
        <Button
          buttonTheme={ButtonThemeEnum.PRIMARY}
          onClick={onSave}
          disabled={isLoading || anyInvalid || !dirty}
        >
          Save
        </Button>
      </Actions>
    </ToolPanel>
  );
}
